'use client';

import { Fallback } from '@cbhq/cds-web/layout/Fallback';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalHeader } from '@cbhq/cds-web/overlays';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { useRouter } from 'next/navigation';
import { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount, useChainId } from 'wagmi';
import { useQueryClient } from '@tanstack/react-query';
import {
  AddLiquidityStateProvider,
  useAddLiquidityState,
} from '@/components/AddLiquidity/AddLiquidityProvider';
import {
  AddLiquidityFormContent,
  AddLiquidityFormContentLoading,
} from '@/components/AddLiquidity/AddLiquidityFormContent';
import { AddLiquidityReviewContent } from '@/components/AddLiquidity/AddLiquidityReviewContent';
import { AddLiquidityConfirmationContent } from '@/components/AddLiquidity/AddLiquidityConfirmationContent';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ERROR_MESSAGE_ADD_LIQUIDITY_FAILED } from '@/app/utils/errors';
import { getPool } from '@/onchain/pools';
import { AddLiquidityParams, LiquidityAction } from '@/types';
import { QUERY_KEY_POSITION_DETAILS } from '@/onchain/hooks/usePositionDetails';
import { useCompliance } from '@/components/ComplianceProvider/ComplianceProvider';
import { useAddLiquidityModal } from '@/components/AddLiquidity/AddLiquidityModalProvider';

type Step = 'form' | 'review' | 'confirm';

/**
 * Inner content component that has access to AddLiquidityStateProvider context.
 * Manages step state and renders content based on current step.
 */
const AddLiquidityInnerContent = memo(function AddLiquidityInnerContent({
  positionId,
  step,
  setStep,
  onCancel,
  onClose,
  onSuccess,
}: {
  positionId: string | null;
  step: Step;
  setStep: (step: Step) => void;
  onCancel: () => void;
  onClose: (event?: { positionId?: bigint }) => void;
  onSuccess: (positionId?: bigint, positionManagerAddress?: `0x${string}`) => void;
}) {
  const chainId = useChainId();
  const { isCompliant, openComplianceModals } = useCompliance();

  const {
    priceInputs,
    priceInputsTicks,
    depositAmountsInput,
    poolState,
    positionTokens,
    isFullRange,
    tickInRange,
    token0AsBaseToken,
    depositAmounts,
    depositAmountState,
    liquidity,
    positionManagerAddress,
  } = useAddLiquidityState();

  // Build addLiquidityParams (extracted from AddLiquidityConfirmButton)
  const addLiquidityParams = useMemo<AddLiquidityParams>(() => {
    return {
      pool: getPool(chainId, positionTokens[0].symbol, positionTokens[1].symbol),
      poolState,
      priceRange: {
        priceLower: isFullRange ? 0 : Number(priceInputs.lower.value),
        priceUpper: isFullRange ? Infinity : Number(priceInputs.upper.value),
        isFullRange,
        priceUnitInAsset0: !token0AsBaseToken,
      },
      asset0: positionTokens[0].asset,
      asset1: positionTokens[1].asset,
      tickLower: token0AsBaseToken ? priceInputsTicks.lower : priceInputsTicks.upper,
      tickUpper: token0AsBaseToken ? priceInputsTicks.upper : priceInputsTicks.lower,
      inRange: tickInRange,
      poolCurrentPrice: Number(priceInputs.current.value),
      asset0DepositAmount: depositAmounts[0],
      asset1DepositAmount: depositAmounts[1],
      asset0DepositAmountHumanReadable: depositAmountsInput[0] ?? '',
      asset1DepositAmountHumanReadable: depositAmountsInput[1] ?? '',
      currencyField: depositAmountState.currency,
      liquidity,
      hookData: '0x',
      liquidityAction: positionId ? LiquidityAction.INCREASE : LiquidityAction.MINT,
      positionId: positionId ? BigInt(positionId) : undefined,
    };
  }, [
    chainId,
    depositAmountState.currency,
    depositAmountsInput,
    depositAmounts,
    isFullRange,
    liquidity,
    poolState,
    positionId,
    positionTokens,
    priceInputs,
    priceInputsTicks.lower,
    priceInputsTicks.upper,
    tickInRange,
    token0AsBaseToken,
  ]);

  // Step handlers
  const handleFormContinue = useCallback(() => {
    if (!isCompliant) {
      openComplianceModals();
    } else {
      setStep('review');
    }
  }, [isCompliant, openComplianceModals, setStep]);

  const handleReviewBack = useCallback(() => {
    setStep('form');
  }, [setStep]);

  const handleReviewConfirm = useCallback(() => {
    setStep('confirm');
  }, [setStep]);

  const handleConfirmSuccess = useCallback(
    (newPositionId?: bigint) => {
      // Notify parent that transaction succeeded (enables X button close)
      // Include positionManagerAddress for navigation
      onSuccess(newPositionId, positionManagerAddress);
    },
    [onSuccess, positionManagerAddress],
  );

  // Render step content
  switch (step) {
    case 'form':
      return (
        <Suspense fallback={<AddLiquidityFormContentLoading positionId={positionId} />}>
          <AddLiquidityFormContent
            positionId={positionId}
            onContinue={handleFormContinue}
            onCancel={onCancel}
          />
        </Suspense>
      );
    case 'review':
      return (
        <div data-testid="add-liquidity-preview-modal">
          <Suspense fallback={<Fallback width="100%" height={400} shape="squircle" />}>
            <AddLiquidityReviewContent onBack={handleReviewBack} onContinue={handleReviewConfirm} />
          </Suspense>
        </div>
      );
    case 'confirm':
      return (
        <div data-testid="add-liquidity-confirmation-modal">
          <AddLiquidityConfirmationContent
            addLiquidityParams={addLiquidityParams}
            open
            onSuccess={handleConfirmSuccess}
            onClose={onClose}
          />
        </div>
      );
  }
});

/**
 * Modal content component that reads params from context
 */
const AddLiquidityModalContent = memo(function AddLiquidityModalContent() {
  const router = useRouter();
  const account = useAccount();
  const chainId = useChainId();
  const queryClient = useQueryClient();
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const { isOpen, params, closeAddLiquidityModal } = useAddLiquidityModal();

  // Extract params
  const positionId = params?.positionId ?? null;
  const poolId = params?.poolId ?? null;
  const currencyFilters = params?.currencyFilters ?? null;
  const positionManagerAddress = params?.positionManagerAddress ?? null;

  // Step state for multi-step modal flow
  const [step, setStep] = useState<Step>('form');
  // Track positionId and positionManagerAddress from successful tx (for navigation on close via X button)
  const [successPositionId, setSuccessPositionId] = useState<bigint | undefined>(undefined);
  const [successPositionManagerAddress, setSuccessPositionManagerAddress] = useState<
    `0x${string}` | undefined
  >(undefined);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setSuccessPositionId(undefined);
      setSuccessPositionManagerAddress(undefined);
    }
  }, [isOpen]);

  // Auto-close modal if wallet disconnects
  useEffect(() => {
    if (!account.isConnected && isOpen) {
      closeAddLiquidityModal();
    }
  }, [account.isConnected, isOpen, closeAddLiquidityModal]);

  // Cancel handler - just close the modal (stay on current page)
  const handleCancel = useCallback(() => {
    closeAddLiquidityModal();
  }, [closeAddLiquidityModal]);

  // Close handler - called when transaction completes or modal is closed after success
  const handleClose = useCallback(
    (event?: { positionId?: bigint }) => {
      if (event?.positionId) {
        void queryClient.resetQueries({
          queryKey: QUERY_KEY_POSITION_DETAILS(chainId, account.address, event.positionId),
        });
        // Use successPositionManagerAddress from the tx, fallback to params for INCREASE flow
        const pmAddress = successPositionManagerAddress ?? positionManagerAddress;
        const positionUrl = pmAddress
          ? `/positions?positionId=${event.positionId.toString()}&positionManagerAddress=${pmAddress}`
          : `/positions?positionId=${event.positionId.toString()}`;
        closeAddLiquidityModal();
        void router.push(positionUrl);
      } else {
        closeAddLiquidityModal();
        void router.push('/portfolio');
      }
    },
    [
      router,
      queryClient,
      account.address,
      chainId,
      successPositionManagerAddress,
      positionManagerAddress,
      closeAddLiquidityModal,
    ],
  );

  // Success handler - called when transaction completes with positionId
  const handleSuccess = useCallback(
    (newPositionId?: bigint, newPositionManagerAddress?: `0x${string}`) => {
      setSuccessPositionId(newPositionId);
      setSuccessPositionManagerAddress(newPositionManagerAddress);
    },
    [],
  );

  // Close handler for X button
  const handleRequestClose = useCallback(() => {
    if (successPositionId !== undefined) {
      // Tx succeeded and we have positionId - go to position page
      handleClose({ positionId: successPositionId });
    } else if (step === 'confirm') {
      // Closing from confirm step without confirmed success - go to portfolio
      // This handles both failed tx AND successful tx waiting for positionId
      closeAddLiquidityModal();
      void router.push('/portfolio');
    } else {
      // Closing from form/review step - just close modal (stay on current page)
      closeAddLiquidityModal();
    }
  }, [successPositionId, step, router, closeAddLiquidityModal, handleClose]);

  // Dynamic title based on step and mode
  const title = useMemo(() => {
    const isCreate = !positionId;
    switch (step) {
      case 'form':
        return isCreate ? 'Create position' : 'Add liquidity';
      case 'review':
        return isCreate ? 'Review position' : 'Review liquidity';
      case 'confirm':
        return 'Confirm in wallet';
    }
  }, [step, positionId]);

  // Don't render if not open or wallet not connected
  if (!isOpen || !account.isConnected) {
    return null;
  }

  return (
    <Modal
      width={isPhone || isPhoneLandscape ? undefined : 650}
      visible={isOpen}
      onRequestClose={handleRequestClose}
      testID="add-liquidity-modal"
    >
      <ModalHeader title={title} />
      <ModalBody>
        <AddLiquidityStateProvider
          positionId={positionId}
          poolId={poolId}
          currencyFilters={currencyFilters}
          positionManagerAddress={positionManagerAddress}
        >
          <VStack width="100%" gap={3}>
            <AddLiquidityInnerContent
              positionId={positionId}
              step={step}
              setStep={setStep}
              onCancel={handleCancel}
              onClose={handleClose}
              onSuccess={handleSuccess}
            />
          </VStack>
        </AddLiquidityStateProvider>
      </ModalBody>
    </Modal>
  );
});

/**
 * Add Liquidity Modal component
 * Reads state from AddLiquidityModalProvider context
 * Should be rendered once in ClientLayout
 */
export const AddLiquidityModal = memo(function AddLiquidityModal() {
  return (
    <ErrorBoundary message={ERROR_MESSAGE_ADD_LIQUIDITY_FAILED}>
      <AddLiquidityModalContent />
    </ErrorBoundary>
  );
});
